/**
 * LoginPage — full-page login screen with logo, form, and cookie consent.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Snackbar,
  Alert,
  Button,
  Paper,
} from "@mui/material";
import LoginForm from "@/components/auth/LoginForm";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Return to the page that required login (e.g. a shared /manual#section link).
  const from = (location.state as { from?: Location } | null)?.from;
  const returnTo = from && from.pathname !== "/login" ? `${from.pathname}${from.search}${from.hash}` : "/";
  const { login, isAuthenticated } = useAuth();
  const { t } = useTranslation("auth");
  const [cookieConsent, setCookieConsent] = useState<boolean>(
    () => localStorage.getItem("cookie-consent") === "true",
  );

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate(returnTo, { replace: true });
  }, [isAuthenticated, navigate, returnTo]);

  const handleLogin = async (credentials: { username: string; password: string }) => {
    const result = await login(credentials);
    // If mustChangePassword is true, redirect to change-password page
    // Otherwise redirect to home
    if (result.mustChangePassword) {
      navigate("/change-password", { replace: true, state: { tempPassword: credentials.password } });
    } else {
      navigate(returnTo, { replace: true });
    }
  };

  const handleCookieAccept = () => {
    localStorage.setItem("cookie-consent", "true");
    setCookieConsent(true);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
        position: "relative",
      }}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <LanguageSwitcher />
      </Box>
      <Card sx={{ maxWidth: 420, width: "100%", mx: "auto" }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Logo / Brand */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              component="img"
              src="/ksena-banner.png"
              alt={t('appTitle')}
              sx={{
                width: "100%",
                maxWidth: 320,
                height: "auto",
                display: "block",
                mx: "auto",
                mb: 2,
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('appSubtitle')}
            </Typography>
          </Box>

          {/* Login heading */}
          <Typography variant="h5" sx={{ mb: 2.5, textAlign: "center" }}>
            {t('loginTitle')}
          </Typography>

          <LoginForm
            onSubmit={handleLogin}
            onForgotPassword={() => navigate("/forgot-password")}
          />
        </CardContent>
      </Card>

      {/* Cookie consent banner */}
      <Snackbar
        open={!cookieConsent}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Paper
          elevation={6}
          sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, maxWidth: 600 }}
        >
          <Alert severity="info" icon={false} sx={{ flex: 1, p: 0, bgcolor: "transparent" }}>
            {t('cookieConsent')}
          </Alert>
          <Button variant="contained" size="small" onClick={handleCookieAccept}>
            {t('cookieAccept')}
          </Button>
        </Paper>
      </Snackbar>
    </Box>
  );
}
